import { useEffect, useRef, type JSX } from 'react';
import { useOutsideClick } from './menu.hooks';

import styles from './menu.module.css';

import { MenuButton, InstallButtonProps } from './button';
import config from '@/config';
import { useDialog } from '@shared/lib/hooks';

/**
 * Open/close is just the dialog's `open` attribute; the fade in both
 * directions is pure CSS (`menu.module.css`'s `.menu-dialog`: an `opacity`
 * transition, with `visibility` flipping to hidden only once the fade-out
 * has finished). There's deliberately no JS closing state: the fade-out used
 * to be driven by setting `data-hide` by hand and closing on `animationend`,
 * and when the toggle button closed the dialog before that animation ran,
 * `data-hide` was left behind and the next open played the fade-*out* -- the
 * menu flashed and closed itself. With the browser owning the transition
 * there's nothing to get out of sync: reopening mid-fade just reverses it.
 */
export function Menu(props: MenuProps) {
	const { isOpen, children, setIsMenuOpen } = props;
	const ref = useRef<HTMLDivElement>(null);
	useMenuFocus(ref, isOpen);

	useOutsideClick(
		ref,
		() => {
			if (isOpen) {
				setIsMenuOpen(false);
			}
		},
		'menu-btn',
	);

	const classNames = [styles['menu']];
	if (isOpen) {
		classNames.push(styles['open']);
	} else {
		classNames.push(styles['close']);
	}

	return (
		<dialog
			open={isOpen}
			className={styles['menu-dialog']}
			onKeyDown={(event) => {
				if (event.key === 'Escape' && isOpen) {
					event.stopPropagation();
					setIsMenuOpen(false);
				}
			}}
		>
			<div ref={ref} className={classNames.join(' ')}>
				{children}
			</div>
		</dialog>
	);
}

const FOCUSABLE =
	'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * Keyboard support for a menu opened from a toggle button: opening moves
 * focus to the first item (otherwise the next Tab skips the menu entirely,
 * since it's rendered away from its toggle), and closing returns it to
 * whatever had it before -- the toggle -- but only when focus was inside
 * the menu, so closing it by clicking elsewhere doesn't pull focus back.
 */
function useMenuFocus(
	ref: React.RefObject<HTMLDivElement | null>,
	isOpen: boolean,
) {
	const returnTo = useRef<HTMLElement | null>(null);

	useEffect(() => {
		const menu = ref.current;
		if (!menu) {
			return;
		}
		if (isOpen) {
			returnTo.current = document.activeElement as HTMLElement | null;
			menu.querySelector<HTMLElement>(FOCUSABLE)?.focus();
			return;
		}
		const focusWasInMenu =
			menu.contains(document.activeElement) ||
			document.activeElement === document.body;
		if (focusWasInMenu && returnTo.current?.isConnected) {
			returnTo.current.focus();
		}
		returnTo.current = null;
	}, [ref, isOpen]);
}

interface MenuProps {
	isOpen: boolean;
	setIsMenuOpen: (a: boolean) => void;
	children?: JSX.Element | JSX.Element[];
}

export function ProfileMenu({
	open: isOpen,
	setOpen: setIsOpen,
	install,
}: ProfileMenuProps) {
	const { setShowDialog } = useDialog();
	if (!config?.app?.auth?.enabled) {
		return null;
	}
	if (config?.app?.auth?.type !== 'cognito') {
		return null;
	}
	return (
		<Menu isOpen={isOpen} setIsMenuOpen={setIsOpen}>
			<MenuButton.SignIn />
			<MenuButton.SignUp />
			<MenuButton.Subscribe />
			<MenuButton.ManageSubscription />
			<MenuButton.Links links={config?.app?.menu?.links} />
			<MenuButton.Install
				show={install?.show}
				onClick={install?.onClick}
			/>
			<MenuButton.Settings onOpen={() => setIsOpen(false)} />
			<MenuButton.About
				onClick={() => {
					setShowDialog(true);
				}}
			/>
			<MenuButton.AccountSignOut />
		</Menu>
	);
}

interface ProfileMenuProps {
	open: boolean;
	setOpen: (v: boolean) => void;
	install?: InstallButtonProps;
}
